import { useEffect, useState, useCallback, useMemo } from "react";
import styles from "./Status.module.css";
import { authFetch } from "../../utils/authFetch";
import RefreshIndicator from '../Modern/Incidents/components/RefreshIndicator';
import usePolling from '../../hooks/usePolling';
import { POLLING_INTERVALS } from '../../config/polling';
import { trackSystemStatusView, trackBuildInfoView } from '../../utils/modernUITracking';

function Status({ user }) {
  const [information, setInformation] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const build = useMemo(() => information.build || {}, [information]);
  const runtime = useMemo(() => information.runtime || {}, [information]);
  const configuration = useMemo(() => information.configuration || {}, [information]);

  const handleCommitLinkClick = useCallback(() => {
    if (build.hash && build.hash !== '__unknown__') {
      trackBuildInfoView({
        buildHash: build.hash,
        region: runtime.region || 'unknown',
        buildBranch: build.branch !== '__unknown__' ? build.branch : 'unknown'
      });
    }
  }, [build, runtime]);

  const fetchStatus = useCallback(async (isBackgroundRefresh = false) => {
    try {
      if (isBackgroundRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const res = await authFetch("/api/v1/status");
      const result = await res.json();

      setInformation(result);
      setLastUpdated(new Date());
      setIsInitialLoad(false);
    } catch (error) {
      console.error("Error fetching status:", error);
      if (isBackgroundRefresh) {
        console.warn('Background refresh failed, keeping existing data:', error);
        throw error;
      }
    } finally {
      if (isBackgroundRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchStatus(false);
  }, [fetchStatus]);

  // Track page view when build info is loaded
  useEffect(() => {
    if (build.hash) {
      trackSystemStatusView({
        region: runtime.region || 'unknown',
        buildBranch: build.branch !== '__unknown__' ? build.branch : 'unknown',
        buildHash: build.hash !== '__unknown__' ? build.hash : 'unknown',
        buildDate: build.date !== '__unknown__' ? build.date : 'unknown'
      });
    }
  }, [build, runtime]);

  // Background auto-refresh
  usePolling(
    () => fetchStatus(true),
    POLLING_INTERVALS.SYSTEM_STATUS,
    {
      enabled: !isInitialLoad && !loading,
      pauseWhenHidden: true
    }
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.pageTitle}>Build Information</h2>
        <RefreshIndicator
          isRefreshing={refreshing}
          lastUpdated={lastUpdated}
          showTimestamp={!refreshing && !!lastUpdated}
        />
      </div>
      <table className={styles.infoTable}>
        <tbody>
          <tr className={styles.tableRow}>
            <td className={styles.tableLabel}>Build Hash</td>
            <td className={styles.tableValue}>
              {build.hash && build.hash !== "__unknown__" ? (
                <a
                  href={
                    "https://ghe.coxautoinc.com/Enterprise-Operations-Center/PBDUBDUBDUB/commit/" +
                    build.hash
                  }
                  className={styles.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleCommitLinkClick}
                >
                  {build.hash}
                </a>
              ) : (
                "Unavailable"
              )}
            </td>
          </tr>
          <tr className={styles.tableRow}>
            <td className={styles.tableLabel}>Build Date</td>
            <td className={styles.tableValue}>
              {build.date && build.date !== "__unknown__" ? build.date : "Unavailable"}
            </td>
          </tr>
          <tr className={styles.tableRow}>
            <td className={styles.tableLabel}>Build Branch</td>
            <td className={styles.tableValue}>
              {build.branch && build.branch !== "__unknown__" ? build.branch : "Unavailable"}
            </td>
          </tr>
          <tr className={styles.tableRow}>
            <td className={styles.tableLabel}>Region</td>
            <td className={styles.tableValue}>{runtime.region || "unknown"}</td>
          </tr>
        </tbody>
      </table>

      <h2 className={styles.sectionTitle}>Environmental Configurations</h2>
      <table className={styles.infoTable}>
        <tbody>
          <tr className={styles.tableRow}>
            <td className={styles.tableLabel}>AWS Support Case Creation</td>
            <td className={styles.tableValue}>
              <span className={configuration.awsSupportCasesEnabled ? styles.badgeEnabled : styles.badgeDisabled}>
                {configuration.awsSupportCasesEnabled ? "AUTOMATIC" : "DISABLED"}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default Status;
